import { Button } from "@/components/ui/je-button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Mail, MapPin, Linkedin, Instagram } from "lucide-react"
import { useServiceSelection } from "@/components/ServiceSelectionProvider"
import { ServiceChip } from "@/components/ui/service-chip"
import { useState, useEffect } from "react"
import { useLanguage } from "@/contexts/LanguageContext"
import { useBotProtection } from "@/hooks/use-bot-protection"
import { useToast } from "@/hooks/use-toast"
import { Honeypot } from "@/components/ui/honeypot"

const ContactSection = () => {
  const { selectedServices, removeService, getMessageText, getServicesCSV } = useServiceSelection()
  const { t } = useLanguage()
  const [messageValue, setMessageValue] = useState("")
  const { toast } = useToast()
  const {
    isSubmitting,
    handleHoneypotChange,
    submitForm,
    honeypotValue,
    enableHoneypot
  } = useBotProtection()

  useEffect(() => {
    const baseMessage = getMessageText()
    setMessageValue(baseMessage)
  }, [selectedServices, getMessageText])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const data = {
      type: 'contact' as const,
      name: formData.get('name') as string,
      prenom: formData.get('prenom') as string,
      email: formData.get('email') as string,
      telephone: formData.get('telephone') as string,
      organization: formData.get('organization') as string,
      budget: formData.get('budget') as string,
      message: formData.get('message') as string,
      services_selected: formData.get('services_selected') as string,
    }

    const result = await submitForm(data)
    
    if (result.success) {
      toast({
        title: "Succès !",
        description: result.message,
      })
      // Reset form
      e.currentTarget.reset()
      setMessageValue(getMessageText())
    } else {
      toast({
        title: "Erreur",
        description: result.error,
        variant: "destructive",
      })
    }
  }
  return (
    <section id="contact" className="py-24 bg-gradient-accent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-section-title text-je-black mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-2xl text-gradient font-bold mb-8">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Formulaire moderne */}
          <div className="card-modern p-10 form-modern">
            <form className="space-y-8" onSubmit={handleSubmit}>
              {/* Honeypot field */}
              {enableHoneypot && (
                <Honeypot 
                  value={honeypotValue}
                  onChange={handleHoneypotChange}
                />
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <Input id="name" name="name" placeholder=" " required className="rounded-xl border-2" />
                  <Label htmlFor="name">{t('contact.name')} *</Label>
                </div>
                <div className="input-group">
                  <Input id="prenom" name="prenom" placeholder=" " required className="rounded-xl border-2" />
                  <Label htmlFor="prenom">{t('contact.firstname')} *</Label>
                </div>
              </div>

              <div className="input-group">
                <Input id="email" name="email" type="email" placeholder=" " required className="rounded-xl border-2" />
                <Label htmlFor="email">{t('contact.email')} *</Label>
              </div>

              <div className="input-group">
                <Input id="telephone" name="telephone" placeholder=" " className="rounded-xl border-2" />
                <Label htmlFor="telephone">{t('contact.phone')}</Label>
              </div>

              <div className="input-group">
                <Input id="organization" name="organization" placeholder=" " className="rounded-xl border-2" />
                <Label htmlFor="organization">{t('contact.organization')}</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget" className="text-je-black font-semibold">{t('contact.budget')}</Label>
                <Select name="budget">
                  <SelectTrigger className="rounded-xl border-2">
                    <SelectValue placeholder={t('contact.budget.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">{t('contact.budget.small')}</SelectItem>
                    <SelectItem value="medium">{t('contact.budget.medium')}</SelectItem>
                    <SelectItem value="large">{t('contact.budget.large')}</SelectItem>
                    <SelectItem value="enterprise">{t('contact.budget.enterprise')}</SelectItem>
                    <SelectItem value="discuss">{t('contact.budget.discuss')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Services sélectionnés */}
              {selectedServices.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-je-black font-semibold">{t('contact.services.selected')}</Label>
                  <div className="flex flex-wrap gap-2" role="list" aria-label={t('contact.services.selected')}>
                    {selectedServices.map((service) => (
                      <ServiceChip
                        key={service.id}
                        service={service.name}
                        onRemove={() => removeService(service.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="input-group">
                <Textarea 
                  id="message" 
                  name="message"
                  rows={6}
                  placeholder=" "
                  required 
                  className="rounded-xl border-2"
                  value={messageValue}
                  onChange={(e) => setMessageValue(e.target.value)}
                />
                <Label htmlFor="message">
                  {t('contact.message')} * {selectedServices.length > 0 && t('contact.message.note')}
                </Label>
              </div>

              {/* Champ caché pour les services */}
              <input 
                type="hidden" 
                name="services_selected" 
                value={getServicesCSV()}
              />

              
              <Button 
                type="submit" 
                size="xl" 
                className="w-full btn-modern shadow-glow"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Envoi en cours..." : t('contact.send')}
              </Button>

            </form>
          </div>

          {/* Informations de contact modernisées */}
          <div className="space-y-8">
            <div className="card-modern p-10">
              <h3 className="text-card-title text-je-black mb-8 font-bold">
                {t('contact.info.title')}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-6 p-4 rounded-xl bg-gradient-accent">
                  <div className="w-12 h-12 bg-je-red rounded-2xl flex items-center justify-center">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-je-black text-lg">{t('contact.info.email')}</p>
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = 'mailto:je.hepia@hesge.ch';
                      }}
                      className="text-muted-foreground hover:text-je-red transition-colors text-lg"
                    >
                      je.hepia@hesge.ch
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-6 p-4 rounded-xl bg-gradient-accent">
                  <div className="w-12 h-12 bg-eth-purple rounded-2xl flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-je-black text-lg">{t('contact.info.address')}</p>
                    <p className="text-muted-foreground text-lg">
                      Rue de la Prairie 4<br />
                      1202 Genève
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <p className="font-bold text-je-black mb-6 text-lg">{t('contact.social')}</p>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.linkedin.com/company/jehepia/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-je-red to-eth-purple rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300"
                    aria-label={t('contact.social') + " LinkedIn"}
                  >
                    <Linkedin className="h-6 w-6 text-white" />
                  </a>
                  <a 
                    href="https://www.instagram.com/jehepia/?hl=fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-eth-green to-eth-blue rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300"
                    aria-label={t('contact.social') + " Instagram"}
                  >
                    <Instagram className="h-6 w-6 text-white" />
                  </a>
                </div>
              </div>
            </div>

            <div className="card-modern p-10 bg-gradient-card">
              <h3 className="text-card-title text-je-black mb-6 font-bold">
                {t('contact.student.title')}
              </h3>
              <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                {t('contact.student.subtitle')}
              </p>
              <Button variant="outline" size="lg" className="w-full btn-modern" asChild>
                <a href="/rejoindre">{t('contact.student.button')}</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactSection